import { FadeIn, Section } from "./ui";
import { ArrowUpRight, Instagram, Linkedin, Facebook, CheckCircle2 } from "lucide-react";
import { useState } from "react";

// Custom TikTok Icon since it might not be in lucide-react standard set
function TikTok(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
    </svg>
  );
}

export function Contact() {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically handle the actual form submission (e.g., to an API)
    setIsSubmitted(true);
  };

  return (
    <Section id="contact" className="pt-24 pb-32">
      <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
        <FadeIn>
          <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-8 leading-[0.9]">
            Vamos <br/>
            <span className="font-highlight italic text-brand-accent lowercase font-normal">conversar?</span>
          </h2>
          <p className="text-brand-light/70 text-xl mb-16 max-w-md font-medium">
            Receba gratuitamente um diagnóstico de marketing para seu negócio. Preencha o formulário e entraremos em contato.
          </p>

          <div className="space-y-12">
            <div>
              <h3 className="text-sm font-bold text-brand-light/50 uppercase tracking-widest mb-2">Email</h3>
              <a href="mailto:contato@p2amarketing.com.br" className="text-xl md:text-2xl font-display font-bold hover:text-brand-accent transition-colors">
                contato@p2amarketing.com.br
              </a>
            </div>
            
            <div>
              <h3 className="text-sm font-bold text-brand-light/50 uppercase tracking-widest mb-2">Telefone / WhatsApp</h3>
              <a 
                href="https://wa.me/5524993144981?text=Vim%20do%20site%20gostaria%20de%20saber%20sobre%20os%20servi%C3%A7os" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-2xl md:text-3xl font-display font-bold hover:text-brand-accent transition-colors"
              >
                +55 24 99314-4981
              </a>
            </div>
          </div>

          <div className="mt-16 flex gap-6">
            <a href="https://www.instagram.com/p2amarketing/" target="_blank" rel="noopener noreferrer" aria-label="Instagram da P2A" className="w-14 h-14 rounded-full border border-brand-light/20 flex items-center justify-center text-brand-light hover:bg-brand-accent hover:border-brand-accent hover:text-brand-light transition-all">
              <Instagram className="w-6 h-6" />
            </a>
            <a href="https://www.facebook.com/profile.php?id=100084326275760&sk=about" target="_blank" rel="noopener noreferrer" aria-label="Facebook da P2A" className="w-14 h-14 rounded-full border border-brand-light/20 flex items-center justify-center text-brand-light hover:bg-brand-accent hover:border-brand-accent hover:text-brand-light transition-all">
              <Facebook className="w-6 h-6" />
            </a>
            <a href="https://www.tiktok.com/@pri_cboaventura" target="_blank" rel="noopener noreferrer" aria-label="TikTok da P2A" className="w-14 h-14 rounded-full border border-brand-light/20 flex items-center justify-center text-brand-light hover:bg-brand-accent hover:border-brand-accent hover:text-brand-light transition-all">
              <TikTok className="w-6 h-6" />
            </a>
          </div>
        </FadeIn>

        <FadeIn delay={0.2}>
          {isSubmitted ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-12 bg-brand-light/5 rounded-[2rem] border border-brand-light/10">
              <div className="w-20 h-20 bg-brand-accent/20 rounded-full flex items-center justify-center mb-6">
                <CheckCircle2 className="w-10 h-10 text-brand-accent" />
              </div>
              <h3 className="text-3xl font-black uppercase tracking-tighter mb-4">Mensagem Enviada!</h3>
              <p className="text-brand-light/70 text-lg">
                Obrigado pelo seu contato. Nossa equipe analisará suas informações e retornará em breve com o seu diagnóstico.
              </p>
              <button 
                onClick={() => setIsSubmitted(false)}
                className="mt-8 px-8 py-3 bg-transparent border border-brand-light/20 rounded-full text-brand-light hover:bg-brand-light/10 transition-colors font-bold uppercase tracking-widest text-sm"
              >
                Enviar nova mensagem
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-10 mt-8 lg:mt-0">
              <div className="relative group">
                <input 
                  type="text" 
                  id="name" 
                  required
                  className="w-full bg-transparent border-b-2 border-brand-light/20 py-4 text-xl md:text-2xl text-brand-light focus:outline-none focus:border-brand-accent transition-colors peer placeholder-transparent"
                  placeholder="Nome Completo"
                />
                <label htmlFor="name" className="absolute left-0 -top-6 text-sm font-bold text-brand-accent uppercase tracking-widest transition-all peer-placeholder-shown:text-xl peer-placeholder-shown:text-brand-light/50 peer-placeholder-shown:top-4 peer-focus:-top-6 peer-focus:text-sm peer-focus:text-brand-accent">
                  Nome Completo
                </label>
              </div>
              
              <div className="relative group">
                <input 
                  type="tel" 
                  id="phone" 
                  required
                  className="w-full bg-transparent border-b-2 border-brand-light/20 py-4 text-xl md:text-2xl text-brand-light focus:outline-none focus:border-brand-accent transition-colors peer placeholder-transparent"
                  placeholder="Telefone / WhatsApp"
                />
                <label htmlFor="phone" className="absolute left-0 -top-6 text-sm font-bold text-brand-accent uppercase tracking-widest transition-all peer-placeholder-shown:text-xl peer-placeholder-shown:text-brand-light/50 peer-placeholder-shown:top-4 peer-focus:-top-6 peer-focus:text-sm peer-focus:text-brand-accent">
                  Telefone / WhatsApp
                </label>
              </div>
              
              <div className="relative group">
                <input 
                  type="email" 
                  id="email" 
                  required
                  className="w-full bg-transparent border-b-2 border-brand-light/20 py-4 text-xl md:text-2xl text-brand-light focus:outline-none focus:border-brand-accent transition-colors peer placeholder-transparent"
                  placeholder="Email Corporativo"
                />
                <label htmlFor="email" className="absolute left-0 -top-6 text-sm font-bold text-brand-accent uppercase tracking-widest transition-all peer-placeholder-shown:text-xl peer-placeholder-shown:text-brand-light/50 peer-placeholder-shown:top-4 peer-focus:-top-6 peer-focus:text-sm peer-focus:text-brand-accent">
                  Email Corporativo
                </label>
              </div>
              
              <div className="relative group">
                <input 
                  type="text" 
                  id="website" 
                  className="w-full bg-transparent border-b-2 border-brand-light/20 py-4 text-xl md:text-2xl text-brand-light focus:outline-none focus:border-brand-accent transition-colors peer placeholder-transparent"
                  placeholder="Site ou Instagram"
                />
                <label htmlFor="website" className="absolute left-0 -top-6 text-sm font-bold text-brand-accent uppercase tracking-widest transition-all peer-placeholder-shown:text-xl peer-placeholder-shown:text-brand-light/50 peer-placeholder-shown:top-4 peer-focus:-top-6 peer-focus:text-sm peer-focus:text-brand-accent">
                  Site ou Instagram
                </label>
              </div>
              
              <div className="relative group pt-4">
                <textarea 
                  id="message" 
                  rows={1}
                  className="w-full bg-transparent border-b-2 border-brand-light/20 py-4 text-xl md:text-2xl text-brand-light focus:outline-none focus:border-brand-accent transition-colors peer placeholder-transparent resize-none"
                  placeholder="Mensagem"
                ></textarea>
                <label htmlFor="message" className="absolute left-0 -top-2 text-sm font-bold text-brand-accent uppercase tracking-widest transition-all peer-placeholder-shown:text-xl peer-placeholder-shown:text-brand-light/50 peer-placeholder-shown:top-8 peer-focus:-top-2 peer-focus:text-sm peer-focus:text-brand-accent">
                  Como podemos ajudar?
                </label>
              </div>
              
              <button type="submit" className="group w-full py-6 bg-brand-light text-brand-dark font-black uppercase tracking-widest rounded-2xl hover:bg-brand-accent hover:text-brand-light transition-all duration-300 flex items-center justify-center gap-4 text-lg mt-8">
                Enviar Solicitação
                <ArrowUpRight className="w-6 h-6 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </button>
            </form>
          )}
        </FadeIn>
      </div>
    </Section>
  );
}
